/**
 * The resource allocation engine: stands, gates, check-in counters and
 * reclaim belts, planned together and re-planned as the day moves.
 *
 * Mirrors the platform's allocation/solver.py, so the demonstration decides
 * exactly as the platform does. Where the established allocators stop, this
 * one carries on:
 *
 *  1. **It plans against what will happen, not what was scheduled.** Every
 *     occupancy is built from the flight record's best time — actual, else
 *     predicted, else estimated, else scheduled — and carries an uncertainty
 *     from that basis. Two flights back to back on one stand are not "clear"
 *     or "in conflict"; they have a CHANCE of meeting, computed from both
 *     uncertainties, and the engine prices that chance. A tight turn behind a
 *     flight with a low-confidence prediction is a risk; the same gap behind
 *     an aircraft already on blocks is not.
 *
 *  2. **It re-plans with the fewest moves.** When a delay, a closure or an
 *     equipment change breaks the plan, only what is broken is re-placed, and
 *     every other move it takes costs something — more the closer it is to
 *     the flight, most of all a gate change after passengers were told. It
 *     will move one other flight out of the way to make room (and says so)
 *     before it sends anybody to a remote stand.
 *
 *  3. **It thinks of towing.** A long stay holding a contact stand that an
 *     arrival needs is split: in, towed to a remote stand, towed back for
 *     departure — offered, never done on its own.
 *
 *  4. **The four resources are one plan.** A stand change re-derives the gate
 *     (a contact stand's own gate, a bus gate for a remote one); counters are
 *     sized from the booked load; a widebody's bags go to a belt that can take
 *     them.
 *
 *  5. **It explains itself.** Every placement carries its cost, term by term,
 *     the alternatives it passed over and why, and a refusal count for every
 *     resource it could not use. An allocator that cannot say "why not 107"
 *     is overridden in a week.
 *
 *  6. **It learns from being overruled**, deterministically: a controller's
 *     override adds a preference for that operator on that resource, counted
 *     and shown, never hidden weights.
 *
 *  7. **Automation is earned, per resource.** Off, advise, or apply on its
 *     own — and even "on its own" refuses tows, late gate changes, and any
 *     change that makes the plan worse, saying which.
 *
 *  8. **Maintenance asks it when.** "Close stand 106 for four hours" returns
 *     the windows that cost the operation least, and what each would cost.
 *
 * Pure. No React, no fetch, and `now` is always passed in.
 */
import { t, tn } from "../i18n";
import { hhmm } from "../lib/format";
import type { SizeCode } from "./flights";
import type { Movement } from "./aodb";

export type ResourceKind = "position" | "gate" | "counter" | "belt";
export const KINDS: ResourceKind[] = ["position", "gate", "counter", "belt"];

export const KIND_LABEL: Record<ResourceKind, string> = {
  position: "Stands",
  gate: "Gates",
  counter: "Check-in counters",
  belt: "Reclaim belts",
};

const SIZES: SizeCode[] = ["A", "B", "C", "D", "E", "F"];
const sizeIx = (s: SizeCode | undefined) => (s ? SIZES.indexOf(s) : 2);

/* ── the register ─────────────────────────────────────────────────────── */

export interface Closure {
  from?: string;
  to?: string;
  why: string;
  ref?: string;
}

export interface Resource {
  kind: ResourceKind;
  code: string;
  name?: string;
  /** the pier, counter island or reclaim hall; counters are allocated in runs within one */
  group?: string;
  /** order within the group, so a run of counters is a run of desks side by side */
  seq?: number;
  /** stands: the largest code letter; belts: the largest unit whose load it takes */
  maxSize?: SizeCode;
  /** a stand with a bridge: people walk rather than ride */
  servedInPlace?: boolean;
  /** what it takes: pax, cargo, ga, maintenance — unset takes anything */
  uses?: string[];
  /** cannot be used at the same time as these */
  excludes?: string[];
  /** a stand's own gate(s) */
  links?: string[];
  /** a gate that serves remote stands by bus */
  bus?: boolean;
  closed?: Closure[];
}

/* ── what has to be placed ────────────────────────────────────────────── */

export type TimeBasis = "actual" | "predicted" | "estimated" | "scheduled";

export interface Demand {
  key: string;
  kind: ResourceKind;
  movementIds: string[];
  label: string;
  operator: string;
  size: SizeCode;
  serviceType: string;
  direction: "arrival" | "departure" | "visit";
  from: string;
  to: string;
  /** minutes of uncertainty at each end, from what the time rests on */
  sigmaStart: number;
  sigmaEnd: number;
  basisStart: TimeBasis;
  basisEnd: TimeBasis;
  /** people carried: bussed from a remote stand, checked in, reclaiming */
  load: number;
  /** desks needed; 1 for everything else */
  units: number;
  /** the time the unit leaves, for a gate: how late a change would be */
  departureAt?: string;
  /** a gate's stand, filled in once the stands are placed */
  positionCode?: string;
  /** already happened or happening: not the engine's to move */
  done?: boolean;
}

export interface Segment {
  codes: string[];
  from: string;
  to: string;
}

export interface Assignment {
  key: string;
  kind: ResourceKind;
  segments: Segment[];
  locked?: boolean;
  source: "engine" | "person" | "auto" | "plan";
  /** set when the engine split a long stay to free the stand */
  tow?: boolean;
}

export interface Rule {
  id: string;
  kind: ResourceKind;
  operator?: string;
  sizes?: SizeCode[];
  serviceType?: string;
  direction?: "arrival" | "departure" | "visit";
  /** resource codes, or "@group" for every resource in a group */
  codes: string[];
  effect: "require" | "prefer" | "avoid" | "forbid";
  weight?: number;
  note: string;
}

export interface Learned {
  kind: ResourceKind;
  operator: string;
  code: string;
  /** times a person put this operator here over the engine's choice */
  n: number;
}

export type Automation = "off" | "advise" | "auto";

export interface Policy {
  kind: ResourceKind;
  automation: Automation;
  /** minutes kept clear between two occupants */
  bufferMin: number;
  /** within this many minutes of its start a placement is not moved */
  freezeMin: number;
  /** a chance of two occupants meeting above this is a problem to solve */
  riskThreshold: number;
  maxAutoMoves: number;
}

export const DEFAULT_POLICIES: Record<ResourceKind, Policy> = {
  position: { kind: "position", automation: "advise", bufferMin: 10, freezeMin: 15, riskThreshold: 0.3, maxAutoMoves: 2 },
  gate: { kind: "gate", automation: "auto", bufferMin: 5, freezeMin: 30, riskThreshold: 0.35, maxAutoMoves: 3 },
  counter: { kind: "counter", automation: "auto", bufferMin: 0, freezeMin: 60, riskThreshold: 0.5, maxAutoMoves: 4 },
  belt: { kind: "belt", automation: "auto", bufferMin: 5, freezeMin: 10, riskThreshold: 0.4, maxAutoMoves: 4 },
};

export const PARAMS = {
  taxiInMin: 5,
  gateOpenBefore: 50,
  gateCloseBefore: 10,
  counterOpenBefore: 180,
  counterCloseBefore: 45,
  /** people one desk checks in an hour */
  counterRate: 30,
  counterMin: 2,
  counterMax: 6,
  beltAfterOnBlock: 10,
  /** a stay at least this long may be split by towing */
  towMinGround: 150,
  towArriveMin: 40,
  towDepartMin: 60,
  /** ground time assumed where the departure is not on the record */
  groundBySize: { A: 40, B: 45, C: 55, D: 80, E: 100, F: 120 } as Record<SizeCode, number>,
};

/* ── the flight record, turned into demands (the airport pack's half) ─── */

/*
 * ICAO designators (A333) and the IATA equipment codes an SSIM file carries
 * (333). A real airline schedule arrives in IATA; with only ICAO here every
 * 777 in one was sized as a narrowbody — found by the stress test's SSIM feed.
 */
const CLASS_SIZE: Record<string, SizeCode> = {
  "318": "C", "319": "C", "320": "C", "321": "C", "32A": "C", "32B": "C", "32N": "C", "32Q": "C", "31N": "C", "738": "C", "73H": "C", "73G": "C", "739": "C", "7M8": "C", "7M9": "C", "E90": "C", "E95": "C", "E75": "C", "AT7": "C", "AT5": "C", "DH4": "C", "CR9": "C", "CRK": "C", "221": "C", "223": "C",
  "763": "D", "764": "D", "752": "D", "753": "D", "310": "D", "757": "D", "767": "D",
  "332": "E", "333": "E", "338": "E", "339": "E", "359": "E", "351": "E", "35K": "E", "772": "E", "773": "E", "77W": "E", "77L": "E", "788": "E", "789": "E", "78X": "E", "781": "E", "744": "E", "74Y": "E",
  "388": "F", "380": "F", "748": "F", "74H": "F",
  C172: "A", PC12: "A", C208: "A", BE20: "B", DH8D: "C", AT72: "C", AT76: "C", E190: "C", E195: "C",
  CRJ9: "C", A319: "C", A320: "C", A20N: "C", A321: "C", A21N: "C", B737: "C", B738: "C", B38M: "C",
  B39M: "C", B739: "C", BCS3: "C", A310: "D", B752: "D", B763: "D", B764: "D", A306: "D",
  A332: "E", A333: "E", A338: "E", A339: "E", A359: "E", A35K: "E", B772: "E", B773: "E", B77W: "E",
  B77L: "E", B788: "E", B789: "E", B78X: "E", B744: "E", MD11: "D", A388: "F", B748: "F",
};

export function sizeOfClass(classKey?: string): SizeCode {
  return (classKey && CLASS_SIZE[classKey.toUpperCase()]) || "C";
}

const MIN = 60_000;
const ms = (iso: string) => Date.parse(iso);
const iso = (n: number) => new Date(n).toISOString();

function sigmaFor(basis: TimeBasis, m: Movement): number {
  if (basis === "actual") return 1;
  if (basis === "predicted") {
    const c = m.prediction?.confidence;
    return c === "high" ? 6 : c === "medium" ? 12 : 20;
  }
  if (basis === "estimated") return 10;
  return m.direction === "arrival" ? 15 : 12;
}

/** The time a movement is best known to happen at, and what that rests on. */
export function bestTime(m: Movement): { at: string; basis: TimeBasis; sigma: number } | null {
  let at: string | undefined;
  let basis: TimeBasis = "scheduled";
  if (m.actualAt) {
    at = m.actualAt;
    basis = "actual";
  } else if (m.predictedAt && m.prediction) {
    at = m.predictedAt;
    basis = "predicted";
  } else if (m.estimatedAt) {
    at = m.estimatedAt;
    basis = "estimated";
  } else if (m.scheduledAt) at = m.scheduledAt;
  if (!at) return null;
  return { at, basis, sigma: sigmaFor(basis, m) };
}

function onBlock(m: Movement) {
  const reached = m.milestones?.reached_position;
  if (reached) return { at: reached, basis: "actual" as TimeBasis, sigma: 1 };
  const b = bestTime(m);
  if (!b) return null;
  return { ...b, at: iso(ms(b.at) + PARAMS.taxiInMin * MIN), sigma: b.basis === "actual" ? 2 : b.sigma };
}

function offBlock(m: Movement) {
  const left = m.milestones?.left_position;
  if (left) return { at: left, basis: "actual" as TimeBasis, sigma: 1 };
  return bestTime(m);
}

const name = (m: Movement) => `${m.carrier}${m.number}`;
const peopleOn = (m: Movement) => m.loads?.actual ?? m.loads?.booked ?? Math.round((m.capacity ?? 150) * 0.8);
const serviceOf = (m: Movement) => m.serviceType || "pax";

/**
 * Stands for each visit (an arrival and its departure together), a gate and
 * counters for each departure carrying people, a belt for each arrival
 * carrying people. Cancelled movements place nothing.
 */
export function demandsFromMovements(
  moves: readonly Movement[],
  sizeOf: (m: Movement) => SizeCode,
  now: Date,
): Demand[] {
  const live = moves.filter((m) => m.status !== "cancelled" && m.status !== "diverted");
  const byId = new Map(live.map((m) => [m.id, m]));
  const pairedDep = new Map<string, Movement>();
  for (const m of live) {
    if (m.direction !== "departure") continue;
    const arr =
      (m.linkedMovementId && byId.get(m.linkedMovementId)) ||
      live.find((a) => a.direction === "arrival" && a.linkedMovementId === m.id);
    if (arr && arr.direction === "arrival") pairedDep.set(arr.id, m);
  }
  const usedDeps = new Set([...pairedDep.values()].map((d) => d.id));
  const out: Demand[] = [];
  const nowMs = now.getTime();

  for (const m of live) {
    if (m.direction === "departure" && usedDeps.has(m.id)) continue;
    const size = sizeOf(m);
    const svc = serviceOf(m);
    if (m.direction === "arrival") {
      const on = onBlock(m);
      if (!on) continue;
      const dep = pairedDep.get(m.id);
      const off = dep ? offBlock(dep) : null;
      const end = off
        ? off
        : { at: iso(ms(on.at) + PARAMS.groundBySize[size] * MIN), basis: on.basis, sigma: on.sigma + 5 };
      const to = ms(end.at) > ms(on.at) ? end.at : iso(ms(on.at) + 30 * MIN);
      out.push({
        key: `position:${m.id}`,
        kind: "position",
        movementIds: dep ? [m.id, dep.id] : [m.id],
        label: dep ? `${name(m)} → ${name(dep)}` : name(m),
        operator: m.carrier,
        size,
        serviceType: svc,
        direction: dep ? "visit" : "arrival",
        from: on.at,
        to,
        sigmaStart: on.sigma,
        sigmaEnd: end.sigma,
        basisStart: on.basis,
        basisEnd: end.basis,
        load: peopleOn(dep ?? m),
        units: 1,
        departureAt: dep ? end.at : undefined,
        done: Boolean(dep?.milestones?.left_position || (dep?.actualAt && dep.status === "departed")) || ms(to) < nowMs - 30 * MIN,
      });
      if (svc === "pax") {
        const load = peopleOn(m);
        const start = ms(on.at) + PARAMS.beltAfterOnBlock * MIN;
        const len = Math.max(20, 15 + Math.round(load / 10));
        out.push({
          key: `belt:${m.id}`,
          kind: "belt",
          movementIds: [m.id],
          label: name(m),
          operator: m.carrier,
          size,
          serviceType: svc,
          direction: "arrival",
          from: iso(start),
          to: iso(start + len * MIN),
          sigmaStart: on.sigma,
          sigmaEnd: on.sigma + 3,
          basisStart: on.basis,
          basisEnd: on.basis,
          load,
          units: 1,
          done: start + len * MIN < nowMs,
        });
      }
    } else {
      /* a departure with no arrival on the record: it is on a stand before it leaves */
      const off = offBlock(m);
      if (!off) continue;
      const ground = Math.max(45, PARAMS.groundBySize[size] - 10);
      /* it comes on stand by the schedule, whatever time it later leaves */
      const onBy = Math.min(ms(off.at), m.scheduledAt ? ms(m.scheduledAt) : ms(off.at)) - ground * MIN;
      out.push({
        key: `position:${m.id}`,
        kind: "position",
        movementIds: [m.id],
        label: name(m),
        operator: m.carrier,
        size,
        serviceType: svc,
        direction: "departure",
        from: iso(onBy),
        to: off.at,
        sigmaStart: 5,
        sigmaEnd: off.sigma,
        basisStart: "scheduled",
        basisEnd: off.basis,
        load: peopleOn(m),
        units: 1,
        departureAt: off.at,
        done: Boolean(m.milestones?.left_position) || ms(off.at) < nowMs - 30 * MIN,
      });
    }
  }

  /* gates and counters, for every departure carrying people */
  for (const m of live) {
    if (m.direction !== "departure" || serviceOf(m) !== "pax") continue;
    const off = offBlock(m);
    if (!off) continue;
    const dep = ms(off.at);
    const load = peopleOn(m);
    const size = sizeOf(m);
    const gone = Boolean(m.milestones?.left_position) || m.status === "departed";
    out.push({
      key: `gate:${m.id}`,
      kind: "gate",
      movementIds: [m.id],
      label: name(m),
      operator: m.carrier,
      size,
      serviceType: "pax",
      direction: "departure",
      from: iso(dep - PARAMS.gateOpenBefore * MIN),
      to: iso(dep - PARAMS.gateCloseBefore * MIN),
      sigmaStart: off.sigma,
      sigmaEnd: off.sigma,
      basisStart: off.basis,
      basisEnd: off.basis,
      load,
      units: 1,
      departureAt: off.at,
      done: gone || dep - PARAMS.gateCloseBefore * MIN < nowMs,
    });
    /* counters open on the SCHEDULE: people come to the airport by the ticket, not the prediction */
    const sched = m.scheduledAt ? ms(m.scheduledAt) : dep;
    const hours = (PARAMS.counterOpenBefore - PARAMS.counterCloseBefore) / 60;
    const desks = Math.min(PARAMS.counterMax, Math.max(PARAMS.counterMin, Math.ceil(load / (PARAMS.counterRate * hours))));
    out.push({
      key: `counter:${m.id}`,
      kind: "counter",
      movementIds: [m.id],
      label: name(m),
      operator: m.carrier,
      size,
      serviceType: "pax",
      direction: "departure",
      from: iso(sched - PARAMS.counterOpenBefore * MIN),
      to: iso(sched - PARAMS.counterCloseBefore * MIN),
      sigmaStart: 0.5,
      sigmaEnd: 0.5,
      basisStart: "scheduled",
      basisEnd: "scheduled",
      load,
      units: desks,
      departureAt: off.at,
      done: gone || sched - PARAMS.counterCloseBefore * MIN < nowMs,
    });
  }
  return out.sort((a, b) => a.from.localeCompare(b.from) || a.key.localeCompare(b.key));
}

/* ── the arithmetic of risk ───────────────────────────────────────────── */

/** Standard normal cumulative distribution (Abramowitz & Stegun 7.1.26). */
export function phi(x: number): number {
  const z = Math.abs(x) / Math.SQRT2;
  const tt = 1 / (1 + 0.3275911 * z);
  const y =
    1 -
    ((((1.061405429 * tt - 1.453152027) * tt + 1.421413741) * tt - 0.284496736) * tt + 0.254829592) *
      tt *
      Math.exp(-z * z);
  return x >= 0 ? (1 + y) / 2 : (1 - y) / 2;
}

/**
 * The chance the earlier occupant is still there, buffer included, when the
 * later one arrives — from the planned gap and both uncertainties.
 */
export function meetRisk(gapMin: number, bufferMin: number, sigmaA: number, sigmaB: number): number {
  const sigma = Math.max(1, Math.sqrt(sigmaA * sigmaA + sigmaB * sigmaB));
  return phi(-(gapMin - bufferMin) / sigma);
}

/* ── the engine ───────────────────────────────────────────────────────── */

export interface Term {
  key: "remote" | "oversize" | "risk" | "rule" | "learned" | "move" | "use" | "tow" | "wide" | "order" | "bus" | "short";
  cost: number;
  text: string;
}

export type RefusalKind = "closed" | "too_big" | "use" | "occupied" | "neighbour" | "rule" | "link" | "none";

export interface Refusal {
  code: string;
  kind: RefusalKind;
  why: string;
  /** the demands in the way, where it is occupied */
  blockers?: string[];
}

export interface Alternative {
  codes: string[];
  cost: number;
  /** what makes it worse than the choice */
  worse: string;
}

export interface Explanation {
  key: string;
  codes: string[];
  cost: number;
  terms: Term[];
  alternatives: Alternative[];
  refusals: Refusal[];
  /** the largest chance of meeting a neighbour, and whom */
  risk: number;
  riskWith?: string;
}

export interface EngineInput {
  resources: readonly Resource[];
  demands: readonly Demand[];
  rules: readonly Rule[];
  learned: readonly Learned[];
  policies: Record<ResourceKind, Policy>;
  /** the plan as it stands; replan keeps it wherever it is not broken */
  current: readonly Assignment[];
  now: Date;
  mode: "plan" | "replan";
  /** kinds the engine may change; the rest are kept exactly as they are */
  kinds?: ResourceKind[];
  /** false skips the per-placement explanations (a search that runs the engine many times) */
  explain?: boolean;
}

export interface Issue {
  key: string;
  kind: "overlap" | "risk" | "closed" | "too_big" | "link" | "unplaced" | "rule";
  why: string;
  risk?: number;
}

export interface Move {
  key: string;
  kind: ResourceKind;
  label: string;
  from: string[];
  to: string[];
  why: string;
  at: string;
  /** what it does to people: a late gate change, a bus instead of a bridge */
  impact?: string;
  tow?: boolean;
}

export interface Score {
  conflicts: number;
  expectedConflicts: number;
  unplaced: number;
  servedInPlacePct: number | null;
  peopleBussed: number;
  tows: number;
  moves: number;
  lateGateChanges: number;
  health: number;
}

export interface EngineResult {
  assignments: Assignment[];
  explain: Record<string, Explanation>;
  unplaced: { key: string; why: string; refusals: Refusal[] }[];
  issues: Issue[];
  moves: Move[];
  score: Score;
  before?: Score;
}

interface Occ {
  key: string;
  from: number;
  to: number;
  sigmaStart: number;
  sigmaEnd: number;
  label: string;
  locked: boolean;
}

class Board {
  byCode = new Map<string, Occ[]>();
  add(a: Assignment, d: Demand, locked: boolean) {
    for (const s of a.segments)
      for (const c of s.codes) {
        const list = this.byCode.get(c) ?? [];
        list.push({
          key: a.key,
          from: ms(s.from),
          to: ms(s.to),
          sigmaStart: s.from === d.from ? d.sigmaStart : 3,
          sigmaEnd: s.to === d.to ? d.sigmaEnd : 3,
          label: d.label,
          locked,
        });
        this.byCode.set(c, list);
      }
  }
  remove(key: string) {
    for (const [c, list] of this.byCode) this.byCode.set(c, list.filter((o) => o.key !== key));
  }
  on(code: string) {
    return this.byCode.get(code) ?? [];
  }
}

const overlapsMs = (a1: number, a2: number, b1: number, b2: number) => a1 < b2 && b1 < a2;

function ruleMatches(r: Rule, d: Demand): boolean {
  if (r.kind !== d.kind) return false;
  if (r.operator && r.operator !== d.operator) return false;
  if (r.sizes?.length && !r.sizes.includes(d.size)) return false;
  if (r.serviceType && r.serviceType !== d.serviceType) return false;
  if (r.direction && r.direction !== d.direction) return false;
  return true;
}

function ruleCovers(r: Rule, res: Resource): boolean {
  return r.codes.includes(res.code) || (!!res.group && r.codes.includes(`@${res.group}`));
}

/** Everything the engine needs about one demand at one candidate, before cost. */
interface Ctx {
  input: EngineInput;
  res: Map<string, Resource>;
  demand: Map<string, Demand>;
  board: Board;
  placed: Map<string, Assignment>;
  current: Map<string, Assignment>;
  byKind: Record<ResourceKind, Resource[]>;
  frozen: Set<string>;
}

function candidates(ctx: Ctx, d: Demand): string[][] {
  const list = ctx.byKind[d.kind];
  if (d.kind !== "counter" || d.units <= 1) return list.map((r) => [r.code]);
  const groups = new Map<string, Resource[]>();
  for (const r of list) groups.set(r.group ?? "", [...(groups.get(r.group ?? "") ?? []), r]);
  const out: string[][] = [];
  for (const rs of groups.values()) {
    rs.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
    /* the full run first; when desks are short, fewer rather than none — at a cost that says so */
    for (let u = d.units; u >= Math.min(d.units, PARAMS.counterMin); u--)
      for (let i = 0; i + u <= rs.length; i++) out.push(rs.slice(i, i + u).map((r) => r.code));
  }
  return out;
}

/** Hard refusals for placing d on these codes over these segments. Empty = feasible. */
function refusalsFor(ctx: Ctx, d: Demand, codes: string[], segs?: Segment[]): Refusal[] {
  const out: Refusal[] = [];
  const windows = segs ?? [{ codes, from: d.from, to: d.to }];
  for (const code of codes) {
    const r = ctx.res.get(code);
    if (!r) {
      out.push({ code, kind: "none", why: t("{code} is not in the register.", { code }) });
      continue;
    }
    for (const w of windows) {
      const f = ms(w.from);
      const to = ms(w.to);
      for (const c of r.closed ?? []) {
        const cf = c.from ? ms(c.from) : -Infinity;
        const ct = c.to ? ms(c.to) : Infinity;
        if (overlapsMs(f, to, cf, ct))
          out.push({
            code,
            kind: "closed",
            why: c.ref
              ? t("{code} is closed ({ref}): {why}", { code, ref: c.ref, why: c.why })
              : t("{code} is closed: {why}", { code, why: c.why }),
          });
      }
    }
    if (d.kind === "position" && r.maxSize && sizeIx(d.size) > sizeIx(r.maxSize))
      out.push({
        code,
        kind: "too_big",
        why: t("{code} takes code {max}; {what} is code {size}.", { code, max: r.maxSize, what: d.label, size: d.size }),
      });
    if (r.uses?.length && !r.uses.includes(d.serviceType))
      out.push({ code, kind: "use", why: t("{code} is not for {use} flights.", { code, use: t(d.serviceType) }) });
    if (d.kind === "gate") {
      const pos = d.positionCode ? ctx.res.get(d.positionCode) : undefined;
      const linked = pos?.links ?? [];
      if (pos?.servedInPlace && linked.length) {
        if (!linked.includes(code))
          out.push({
            code,
            kind: "link",
            why: t("{what} is on {stand}, which is reached through gate {gate}.", {
              what: d.label,
              stand: d.positionCode ?? "",
              gate: linked.join(", "),
            }),
          });
      } else if (!r.bus)
        out.push(d.positionCode ? {
          code,
          kind: "link",
          why: t("{what} is on remote stand {stand}; it needs a bus gate.", { what: d.label, stand: d.positionCode }),
        } : { code, kind: "link", why: t("{what} has no stand yet; only a bus gate can serve it.", { what: d.label }) });
    }
  }
  for (const rule of ctx.input.rules) {
    if (!ruleMatches(rule, d)) continue;
    const covered = codes.every((c) => {
      const r = ctx.res.get(c);
      return r ? ruleCovers(rule, r) : false;
    });
    if (rule.effect === "require" && !covered)
      out.push({ code: codes[0], kind: "rule", why: t("Rule: {note}", { note: rule.note }) });
    if (rule.effect === "forbid" && codes.some((c) => ctx.res.get(c) && ruleCovers(rule, ctx.res.get(c)!)))
      out.push({ code: codes[0], kind: "rule", why: t("Rule: {note}", { note: rule.note }) });
  }
  for (const w of windows) {
    const f = ms(w.from);
    const to = ms(w.to);
    for (const code of w.codes) {
      const hits = ctx.board.on(code).filter((o) => o.key !== d.key && overlapsMs(f, to, o.from, o.to));
      if (hits.length)
        out.push({
          code,
          kind: "occupied",
          blockers: [...new Set(hits.map((h) => h.key))],
          why: t("{code} is taken by {who} from {from} to {to}.", {
            code,
            who: hits.map((h) => h.label).join(", "),
            from: hhmm(iso(hits[0].from)),
            to: hhmm(iso(hits[0].to)),
          }),
        });
      for (const ex of ctx.res.get(code)?.excludes ?? []) {
        const near = ctx.board.on(ex).filter((o) => o.key !== d.key && overlapsMs(f, to, o.from, o.to));
        if (near.length)
          out.push({
            code,
            kind: "neighbour",
            blockers: [...new Set(near.map((h) => h.key))],
            why: t("{code} cannot be used while {other} is occupied by {who}.", {
              code,
              other: ex,
              who: near.map((h) => h.label).join(", "),
            }),
          });
      }
    }
  }
  return out;
}

/** The chance of meeting the neighbours on each side, on the codes and their exclusions. */
function riskAt(ctx: Ctx, d: Demand, codes: string[], segs?: Segment[]): { risk: number; sum: number; with?: string } {
  const pol = ctx.input.policies[d.kind];
  const windows = segs ?? [{ codes, from: d.from, to: d.to }];
  let worst = 0;
  let sum = 0;
  let who: string | undefined;
  for (const w of windows) {
    const f = ms(w.from);
    const to = ms(w.to);
    const sStart = w.from === d.from ? d.sigmaStart : 3;
    const sEnd = w.to === d.to ? d.sigmaEnd : 3;
    for (const code of w.codes) {
      const related = [code, ...(ctx.res.get(code)?.excludes ?? [])];
      for (const rc of related) {
        for (const o of ctx.board.on(rc)) {
          if (o.key === d.key) continue;
          let r = 0;
          if (o.to <= f) r = meetRisk((f - o.to) / MIN, pol.bufferMin, o.sigmaEnd, sStart);
          else if (o.from >= to) r = meetRisk((o.from - to) / MIN, pol.bufferMin, sEnd, o.sigmaStart);
          else r = 1;
          if (r < 0.01) continue;
          sum += r;
          if (r > worst) {
            worst = r;
            who = o.label;
          }
        }
      }
    }
  }
  return { risk: worst, sum, with: who };
}

const RISK_WEIGHT: Record<ResourceKind, number> = { position: 250, gate: 150, counter: 30, belt: 60 };
const MOVE_BASE: Record<ResourceKind, number> = { position: 40, gate: 30, counter: 20, belt: 15 };

function costAt(ctx: Ctx, d: Demand, codes: string[], segs?: Segment[]): { cost: number; terms: Term[]; risk: number; riskWith?: string } {
  const terms: Term[] = [];
  const first = ctx.res.get(codes[0]);
  if (d.kind === "position" && first) {
    if (!first.servedInPlace && d.serviceType === "pax") {
      const c = 30 + 0.25 * d.load;
      terms.push({ key: "remote", cost: c, text: t("remote: {n} people by bus", { n: d.load }) });
    }
    const slack = sizeIx(first.maxSize ?? "F") - sizeIx(d.size);
    if (slack > 0) terms.push({ key: "oversize", cost: 8 * slack, text: tn(slack, "keeps a stand {n} size larger busy", "keeps a stand {n} sizes larger busy") });
    if (d.serviceType === "cargo" && !(first.uses ?? []).includes("cargo"))
      terms.push({ key: "use", cost: 40, text: t("a cargo flight away from the cargo stands") });
  }
  if (d.kind === "belt" && first && first.maxSize && sizeIx(d.size) > sizeIx(first.maxSize))
    terms.push({ key: "wide", cost: 60, text: t("a widebody's bags on a short belt: slower reclaim") });
  if (d.kind === "belt" && first) {
    const slack = sizeIx(first.maxSize ?? "F") - Math.max(sizeIx(d.size), 3);
    if (slack > 0 && sizeIx(d.size) < 3) terms.push({ key: "wide", cost: 10, text: t("uses a belt kept for widebodies") });
  }
  if (d.kind === "counter" && first) {
    terms.push({ key: "order", cost: 0.05 * (first.seq ?? 0), text: t("desk order") });
    const short = d.units - codes.length;
    if (short > 0)
      terms.push({ key: "short", cost: 25 * short, text: tn(short, "{n} desk fewer than the load needs: longer queues", "{n} desks fewer than the load needs: longer queues") });
  }
  const rk = riskAt(ctx, d, codes, segs);
  if (rk.sum > 0)
    terms.push({
      key: "risk",
      cost: RISK_WEIGHT[d.kind] * rk.sum,
      text: t("{pct}% chance of meeting {who}", { pct: Math.round(rk.risk * 100), who: rk.with ?? "" }),
    });
  for (const rule of ctx.input.rules) {
    if (!ruleMatches(rule, d) || (rule.effect !== "prefer" && rule.effect !== "avoid")) continue;
    const covered = codes.every((c) => {
      const r = ctx.res.get(c);
      return r ? ruleCovers(rule, r) : false;
    });
    const w = rule.weight ?? 25;
    if (rule.effect === "prefer" && covered) terms.push({ key: "rule", cost: -w, text: t("preferred: {note}", { note: rule.note }) });
    if (rule.effect === "prefer" && !covered) terms.push({ key: "rule", cost: 0, text: "" });
    if (rule.effect === "avoid" && covered) terms.push({ key: "rule", cost: w, text: t("avoided: {note}", { note: rule.note }) });
  }
  for (const l of ctx.input.learned) {
    if (l.kind === d.kind && l.operator === d.operator && codes.includes(l.code) && l.n > 0) {
      terms.push({
        key: "learned",
        cost: -Math.min(30, 6 * l.n),
        text: t("learned: controllers put {op} here {times}", { op: d.operator, times: tn(l.n, "{n} time", "{n} times") }),
      });
    }
  }
  const cur = ctx.current.get(d.key);
  if (ctx.input.mode === "replan" && cur) {
    const was = cur.segments.at(-1)?.codes ?? [];
    const same = was.length === codes.length && was.every((c, i) => c === codes[i]) && !segs;
    if (!same) {
      const now = ctx.input.now.getTime();
      const soon = ms(d.from) - now < 90 * MIN;
      let c = MOVE_BASE[d.kind] + (soon ? 60 : 0);
      let text = soon ? t("a change within 90 minutes of the flight") : t("a change to the plan");
      if (d.kind === "gate" && d.departureAt && ms(d.departureAt) - now < 60 * MIN) {
        c += 120;
        text = t("a gate change after passengers were told");
      }
      terms.push({ key: "move", cost: c, text });
    }
  }
  if (segs && segs.length > 1) terms.push({ key: "tow", cost: 60, text: t("two tows") });
  const cost = terms.reduce((n, x) => n + x.cost, 0);
  return { cost, terms: terms.filter((x) => x.text), risk: rk.risk, riskWith: rk.with };
}

function refusalText(rs: Refusal[]): string {
  const by = new Map<RefusalKind, Refusal[]>();
  for (const r of rs) by.set(r.kind, [...(by.get(r.kind) ?? []), r]);
  const parts: string[] = [];
  const codes = (list: Refusal[]) => [...new Set(list.map((r) => r.code))];
  const n = (k: RefusalKind) => codes(by.get(k) ?? []).length;
  if (n("too_big")) parts.push(tn(n("too_big"), "{n} too small", "{n} too small"));
  if (n("occupied")) parts.push(tn(n("occupied"), "{n} taken", "{n} taken"));
  if (n("neighbour")) parts.push(tn(n("neighbour"), "{n} blocked by a neighbour", "{n} blocked by a neighbour"));
  if (n("closed")) parts.push(tn(n("closed"), "{n} closed", "{n} closed"));
  if (n("link")) parts.push(tn(n("link"), "{n} not reachable from its stand", "{n} not reachable from its stand"));
  if (n("use")) parts.push(tn(n("use"), "{n} not for this kind of flight", "{n} not for this kind of flight"));
  if (n("rule")) parts.push(tn(n("rule"), "{n} ruled out by a rule", "{n} ruled out by a rule"));
  return parts.join(" · ");
}

function explainFor(ctx: Ctx, d: Demand, codes: string[], segs?: Segment[]): Explanation {
  const chosen = costAt(ctx, d, codes, segs);
  const alts: Alternative[] = [];
  const refusals: Refusal[] = [];
  for (const c of candidates(ctx, d)) {
    if (c.join() === codes.join()) continue;
    const rs = refusalsFor(ctx, d, c);
    if (rs.length) {
      refusals.push(...rs);
      continue;
    }
    const k = costAt(ctx, d, c);
    /* what differs from the choice: costs it adds, and advantages it loses */
    const had = new Set(chosen.terms.map((x) => x.text));
    const has = new Set(k.terms.map((x) => x.text));
    const adds = k.terms.filter((x) => x.cost > 0 && !had.has(x.text)).sort((a, b) => b.cost - a.cost).map((x) => x.text);
    const loses = chosen.terms.filter((x) => x.cost < 0 && !has.has(x.text)).map((x) => t("not {what}", { what: x.text }));
    const worst = [...adds, ...loses].slice(0, 2).join("; ");
    alts.push({ codes: c, cost: Math.round(k.cost), worse: worst || t("no better on any count") });
  }
  alts.sort((a, b) => a.cost - b.cost);
  return {
    key: d.key,
    codes,
    cost: Math.round(chosen.cost),
    terms: chosen.terms.map((x) => ({ ...x, cost: Math.round(x.cost) })),
    alternatives: alts.slice(0, 3),
    refusals,
    risk: chosen.risk,
    riskWith: chosen.riskWith,
  };
}

function bestFor(ctx: Ctx, d: Demand, exclude?: Set<string>): { codes: string[]; cost: number } | null {
  let best: { codes: string[]; cost: number } | null = null;
  for (const c of candidates(ctx, d)) {
    if (exclude && c.some((x) => exclude.has(x))) continue;
    if (refusalsFor(ctx, d, c).length) continue;
    const k = costAt(ctx, d, c).cost;
    if (!best || k < best.cost - 1e-9) best = { codes: c, cost: k };
  }
  return best;
}

function place(ctx: Ctx, d: Demand, codes: string[], source: Assignment["source"], segs?: Segment[]) {
  const a: Assignment = {
    key: d.key,
    kind: d.kind,
    segments: segs ?? [{ codes, from: d.from, to: d.to }],
    source,
    ...(segs && segs.length > 1 ? { tow: true } : {}),
  };
  ctx.placed.set(d.key, a);
  ctx.board.add(a, d, ctx.frozen.has(d.key));
}

function unplace(ctx: Ctx, key: string) {
  ctx.placed.delete(key);
  ctx.board.remove(key);
}

function totalCost(ctx: Ctx, keys: Iterable<string>): number {
  let n = 0;
  for (const k of keys) {
    const a = ctx.placed.get(k);
    const d = ctx.demand.get(k);
    if (!a || !d) continue;
    const segs = a.segments.length > 1 ? a.segments : undefined;
    n += costAt(ctx, d, a.segments.at(-1)!.codes, segs).cost;
  }
  return n;
}

/** Keys whose cost can change when this one moves: itself and whoever shares a code. */
function neighbourhood(ctx: Ctx, key: string, extra: string[][] = []): Set<string> {
  const out = new Set([key]);
  const a = ctx.placed.get(key);
  const codes = new Set<string>([...(a?.segments.flatMap((s) => s.codes) ?? []), ...extra.flat()]);
  for (const c of [...codes]) for (const ex of ctx.res.get(c)?.excludes ?? []) codes.add(ex);
  for (const c of codes) for (const o of ctx.board.on(c)) out.add(o.key);
  return out;
}

function tryEject(ctx: Ctx, d: Demand): boolean {
  let best: { cost: number; apply: () => void } | null = null;
  for (const c of candidates(ctx, d)) {
    const rs = refusalsFor(ctx, d, c);
    if (!rs.length) continue;
    if (rs.some((r) => r.kind !== "occupied" && r.kind !== "neighbour")) continue;
    const blockers = [...new Set(rs.flatMap((r) => r.blockers ?? []))];
    if (!blockers.length || blockers.length > 2 || blockers.some((b) => ctx.frozen.has(b))) continue;
    const saved = blockers.map((b) => ({ a: ctx.placed.get(b)!, d: ctx.demand.get(b)! }));
    if (saved.some((s) => !s.a || !s.d)) continue;
    const scope = new Set<string>([d.key, ...blockers]);
    for (const b of blockers) for (const k of neighbourhood(ctx, b)) scope.add(k);
    for (const k of neighbourhood(ctx, d.key, [c])) scope.add(k);
    const before = totalCost(ctx, scope);
    for (const s of saved) unplace(ctx, s.d.key);
    if (refusalsFor(ctx, d, c).length) {
      for (const s of saved) {
        ctx.placed.set(s.d.key, s.a);
        ctx.board.add(s.a, s.d, false);
      }
      continue;
    }
    place(ctx, d, c, "engine");
    const moved: { d: Demand; codes: string[] }[] = [];
    let ok = true;
    for (const s of saved) {
      const alt = bestFor(ctx, s.d);
      if (!alt) {
        ok = false;
        break;
      }
      place(ctx, s.d, alt.codes, "engine");
      moved.push({ d: s.d, codes: alt.codes });
    }
    const after = ok ? totalCost(ctx, scope) : Infinity;
    /* undo, then remember the best */
    unplace(ctx, d.key);
    for (const m of moved) unplace(ctx, m.d.key);
    for (const s of saved) {
      ctx.placed.set(s.d.key, s.a);
      ctx.board.add(s.a, s.d, false);
    }
    if (!ok) continue;
    const delta = after - before;
    if (!best || delta < best.cost) {
      const keep = { c, moved, saved };
      best = {
        cost: delta,
        apply: () => {
          for (const s of keep.saved) unplace(ctx, s.d.key);
          place(ctx, d, keep.c, "engine");
          for (const m of keep.moved) place(ctx, m.d, m.codes, "engine");
        },
      };
    }
  }
  if (!best) return false;
  best.apply();
  return true;
}

/** Split a long stay to free its stand for d: in, towed away, towed back. */
function tryTow(ctx: Ctx, d: Demand): boolean {
  if (d.kind !== "position") return false;
  const pol = ctx.input.policies.position;
  for (const c of candidates(ctx, d)) {
    const rs = refusalsFor(ctx, d, c);
    if (!rs.length || rs.some((r) => r.kind !== "occupied")) continue;
    const blockers = [...new Set(rs.flatMap((r) => r.blockers ?? []))];
    if (blockers.length !== 1) continue;
    const b = ctx.demand.get(blockers[0]);
    const ba = ctx.placed.get(blockers[0]);
    /* a unit already on stand may still be towed: the tow changes only the hours ahead */
    if (!b || !ba || ba.segments.length > 1 || ba.locked || b.done) continue;
    const ground = (ms(b.to) - ms(b.from)) / MIN;
    if (ground < PARAMS.towMinGround) continue;
    const m1 = Math.max(ms(b.from) + PARAMS.towArriveMin * MIN, ctx.input.now.getTime() + pol.freezeMin * MIN);
    const m2 = ms(b.to) - PARAMS.towDepartMin * MIN;
    if (!(m1 <= ms(d.from) - pol.bufferMin * MIN && ms(d.to) + pol.bufferMin * MIN <= m2)) continue;
    const code = c[0];
    const saved = ba;
    unplace(ctx, b.key);
    const mid: Segment = { codes: [], from: iso(m1), to: iso(m2) };
    let parking: string | null = null;
    let bestCost = Infinity;
    for (const r of ctx.byKind.position) {
      if (r.code === code) continue;
      if (refusalsFor(ctx, b, [r.code], [{ codes: [r.code], from: mid.from, to: mid.to }]).length) continue;
      const k = (r.servedInPlace ? 50 : 0) + costAt(ctx, b, [r.code], [{ codes: [r.code], from: mid.from, to: mid.to }]).cost;
      if (k < bestCost) {
        bestCost = k;
        parking = r.code;
      }
    }
    if (!parking) {
      ctx.placed.set(b.key, saved);
      ctx.board.add(saved, b, false);
      continue;
    }
    const segs: Segment[] = [
      { codes: [code], from: b.from, to: iso(m1) },
      { codes: [parking], from: iso(m1), to: iso(m2) },
      { codes: [code], from: iso(m2), to: b.to },
    ];
    place(ctx, b, [code], "engine", segs);
    if (refusalsFor(ctx, d, c).length) {
      unplace(ctx, b.key);
      ctx.placed.set(b.key, saved);
      ctx.board.add(saved, b, false);
      continue;
    }
    place(ctx, d, c, "engine");
    return true;
  }
  return false;
}

function improve(ctx: Ctx, keys: string[], passes = 3) {
  for (let p = 0; p < passes; p++) {
    let better = false;
    for (const k of keys) {
      const d = ctx.demand.get(k);
      const a = ctx.placed.get(k);
      if (!d || !a || a.segments.length > 1 || ctx.frozen.has(k)) continue;
      const was = a.segments[0].codes;
      let bestCodes = was;
      let bestDelta = -0.5;
      for (const c of candidates(ctx, d)) {
        if (c.join() === was.join()) continue;
        const scope = neighbourhood(ctx, k, [c]);
        const before = totalCost(ctx, scope);
        unplace(ctx, k);
        if (refusalsFor(ctx, d, c).length) {
          ctx.placed.set(k, a);
          ctx.board.add(a, d, false);
          continue;
        }
        place(ctx, d, c, a.source);
        const after = totalCost(ctx, scope);
        unplace(ctx, k);
        ctx.placed.set(k, a);
        ctx.board.add(a, d, false);
        if (after - before < bestDelta) {
          bestDelta = after - before;
          bestCodes = c;
        }
      }
      if (bestCodes !== was) {
        unplace(ctx, k);
        place(ctx, d, bestCodes, "engine");
        better = true;
      }
    }
    /* swaps: two single-code placements trading places */
    for (let i = 0; i < keys.length; i++) {
      for (let j = i + 1; j < keys.length; j++) {
        const ka = keys[i];
        const kb = keys[j];
        const da = ctx.demand.get(ka);
        const db = ctx.demand.get(kb);
        const aa = ctx.placed.get(ka);
        const ab = ctx.placed.get(kb);
        if (!da || !db || !aa || !ab || da.kind !== db.kind || da.units !== db.units) continue;
        if (aa.segments.length > 1 || ab.segments.length > 1 || ctx.frozen.has(ka) || ctx.frozen.has(kb)) continue;
        const ca = aa.segments[0].codes;
        const cb = ab.segments[0].codes;
        if (ca.join() === cb.join()) continue;
        const scope = new Set([...neighbourhood(ctx, ka, [cb]), ...neighbourhood(ctx, kb, [ca])]);
        const before = totalCost(ctx, scope);
        unplace(ctx, ka);
        unplace(ctx, kb);
        let ok = !refusalsFor(ctx, da, cb).length;
        if (ok) {
          place(ctx, da, cb, "engine");
          ok = !refusalsFor(ctx, db, ca).length;
          if (ok) place(ctx, db, ca, "engine");
        }
        const after = ok ? totalCost(ctx, scope) : Infinity;
        if (ok && after - before < -0.5) {
          better = true;
          continue;
        }
        unplace(ctx, ka);
        unplace(ctx, kb);
        ctx.placed.set(ka, aa);
        ctx.board.add(aa, da, false);
        ctx.placed.set(kb, ab);
        ctx.board.add(ab, db, false);
      }
    }
    if (!better) break;
  }
}

/* ── reading a plan ───────────────────────────────────────────────────── */

function freezeSet(input: EngineInput, demands: Map<string, Demand>, current: Map<string, Assignment>): Set<string> {
  const now = input.now.getTime();
  const out = new Set<string>();
  const allowed = new Set(input.kinds ?? KINDS);
  for (const d of demands.values()) {
    const cur = current.get(d.key);
    if (!allowed.has(d.kind) && cur) out.add(d.key);
    if (!cur) continue;
    if (cur.locked || d.done) out.add(d.key);
    else if (d.basisStart === "actual" && ms(d.from) <= now) out.add(d.key);
    else if (input.mode === "replan" && ms(d.from) - input.policies[d.kind].freezeMin * MIN <= now) out.add(d.key);
  }
  return out;
}

export function retime(a: Assignment, d: Demand): Assignment {
  if (a.segments.length === 1) return { ...a, segments: [{ ...a.segments[0], from: d.from, to: d.to }] };
  const segs = a.segments.map((s) => ({ ...s }));
  segs[0].from = d.from;
  segs[segs.length - 1].to = d.to;
  /* a tow whose stay shrank below its tow times is no longer a tow */
  if (ms(segs[0].to) >= ms(segs[segs.length - 1].from) || ms(segs[0].to) <= ms(d.from) || ms(segs.at(-1)!.from) >= ms(d.to))
    return { ...a, tow: undefined, segments: [{ codes: segs[0].codes, from: d.from, to: d.to }] };
  return { ...a, segments: segs };
}

function makeCtx(input: EngineInput): Ctx {
  const res = new Map(input.resources.map((r) => [r.code, r]));
  const demand = new Map(input.demands.map((d) => [d.key, { ...d }]));
  /*
   * A stored placement follows its flight's CURRENT times, not the times it
   * was planned on: the plan says which stand, the flight record says when.
   * A plan kept at its planning-time occupancy never sees the delay that
   * breaks it.
   */
  const current = new Map(
    input.current.filter((a) => demand.has(a.key)).map((a) => [a.key, retime(a, demand.get(a.key)!)]),
  );
  const byKind = { position: [], gate: [], counter: [], belt: [] } as Record<ResourceKind, Resource[]>;
  for (const r of input.resources) byKind[r.kind].push(r);
  return {
    input,
    res,
    demand,
    board: new Board(),
    placed: new Map(),
    current,
    byKind,
    frozen: freezeSet(input, demand, current),
  };
}

/** What is wrong with the plan as it stands, found before anything is moved. */
export function review(input: EngineInput): Issue[] {
  const ctx = makeCtx({ ...input, mode: "replan" });
  for (const a of ctx.current.values()) ctx.board.add(a, ctx.demand.get(a.key)!, ctx.frozen.has(a.key));
  for (const a of ctx.current.values()) ctx.placed.set(a.key, a);
  return issuesOf(ctx);
}

function issuesOf(ctx: Ctx): Issue[] {
  const out: Issue[] = [];
  for (const d of ctx.demand.values()) if (d.kind === "gate") d.positionCode = positionOf(ctx, d);
  const seenPair = new Set<string>();
  for (const d of ctx.demand.values()) {
    const a = ctx.placed.get(d.key);
    if (!a) {
      if (!d.done) out.push({ key: d.key, kind: "unplaced", why: t("{what} has no {kind}.", { what: d.label, kind: t(KIND_NOUN[d.kind]) }) });
      continue;
    }
    if (d.done) continue;
    const segs = a.segments.length > 1 ? a.segments : undefined;
    const codes = a.segments.at(-1)!.codes;
    const rs = refusalsFor(ctx, d, codes, segs);
    for (const r of rs) {
      if (r.kind === "occupied" || r.kind === "neighbour") {
        const pair = [d.key, ...(r.blockers ?? [])].sort().join("|");
        if (seenPair.has(pair)) continue;
        seenPair.add(pair);
        /* the later of the two is the one to move */
        const others = (r.blockers ?? []).map((b) => ctx.demand.get(b)).filter(Boolean) as Demand[];
        const later = [d, ...others].filter((x) => !ctx.frozen.has(x.key)).sort((x, y) => y.from.localeCompare(x.from))[0] ?? d;
        const names = [d, ...others].map((x) => x.label);
        out.push({
          key: later.key,
          kind: "overlap",
          why:
            r.kind === "neighbour"
              ? r.why
              : names.length === 2
                ? t("{a} and {b} are both on {code} at {at}.", { a: names[0], b: names[1], code: r.code, at: hhmm(later.from) })
                : t("{list} and {last} are all on {code} at {at}.", {
                    list: names.slice(0, -1).join(", "),
                    last: names[names.length - 1],
                    code: r.code,
                    at: hhmm(later.from),
                  }),
        });
      } else if (r.kind === "closed") out.push({ key: d.key, kind: "closed", why: r.why });
      else if (r.kind === "too_big") out.push({ key: d.key, kind: "too_big", why: r.why });
      else if (r.kind === "link") out.push({ key: d.key, kind: "link", why: r.why });
      else if (r.kind === "rule") out.push({ key: d.key, kind: "rule", why: r.why });
    }
    if (!rs.length) {
      const pol = ctx.input.policies[d.kind];
      const rk = riskAt(ctx, d, codes, segs);
      if (rk.risk > pol.riskThreshold) {
        /* found from both sides: keep the pair once, on the later */
        const pair = [d.label, rk.with ?? ""].sort().join("|") + d.kind;
        if (seenPair.has(pair)) continue;
        seenPair.add(pair);
        const other = [...ctx.demand.values()].find((x) => x.kind === d.kind && x.label === rk.with);
        const later = other && !ctx.frozen.has(other.key) && other.from > d.from ? other : d;
        if (ctx.frozen.has(later.key)) continue;
        out.push({
          key: later.key,
          kind: "risk",
          risk: rk.risk,
          why: t("{pct}% chance {a} and {b} meet on {code}.", {
            pct: Math.round(rk.risk * 100),
            a: d.label,
            b: rk.with ?? "",
            code: codes.join(", "),
          }),
        });
      }
    }
  }
  return out;
}

export const KIND_NOUN: Record<ResourceKind, string> = {
  position: "stand",
  gate: "gate",
  counter: "counters",
  belt: "belt",
};

function scoreOf(ctx: Ctx, moves: number, lateGate: number): Score {
  const issues = issuesOf(ctx);
  const conflicts = issues.filter((i) => i.kind === "overlap" || i.kind === "closed" || i.kind === "too_big" || i.kind === "link").length;
  const unplaced = issues.filter((i) => i.kind === "unplaced").length;
  let expected = 0;
  let pax = 0;
  let served = 0;
  let bussed = 0;
  let tows = 0;
  for (const [k, a] of ctx.placed) {
    const d = ctx.demand.get(k);
    if (!d || d.done) continue;
    const segs = a.segments.length > 1 ? a.segments : undefined;
    expected += riskAt(ctx, d, a.segments.at(-1)!.codes, segs).sum / 2;
    if (a.tow) tows++;
    if (d.kind === "position" && d.serviceType === "pax") {
      pax++;
      const r = ctx.res.get(a.segments[0].codes[0]);
      if (r?.servedInPlace) served++;
      else bussed += d.load;
    }
  }
  const servedPct = pax ? Math.round((served / pax) * 100) : null;
  const health = Math.max(
    0,
    Math.min(100, Math.round(100 - 20 * conflicts - 10 * unplaced - 20 * expected - 0.3 * (100 - (servedPct ?? 100)) - 2 * tows)),
  );
  return {
    conflicts,
    expectedConflicts: Math.round(expected * 100) / 100,
    unplaced,
    servedInPlacePct: servedPct,
    peopleBussed: bussed,
    tows,
    moves,
    lateGateChanges: lateGate,
    health,
  };
}

/**
 * Plan (everything not locked, from scratch) or re-plan (keep the plan, fix
 * what is broken with the fewest moves). Returns the plan, why each
 * placement, what it could not place and why, what moved, and the score.
 */
export function solve(input: EngineInput): EngineResult {
  const ctx = makeCtx(input);
  const allowed = new Set(input.kinds ?? KINDS);

  /* before: the plan as it stands, scored */
  const pre = makeCtx({ ...input, mode: "replan" });
  for (const a of pre.current.values()) {
    pre.placed.set(a.key, a);
    pre.board.add(a, pre.demand.get(a.key)!, pre.frozen.has(a.key));
  }
  const issues = issuesOf(pre);
  const before = scoreOf(pre, 0, 0);

  /* a broken placement is fixed however close it is, unless the unit is already there */
  const nowMs0 = input.now.getTime();
  for (const i of issues) {
    const d = ctx.demand.get(i.key);
    if (!d || d.done || i.kind === "risk" || i.kind === "unplaced") continue;
    if (d.basisStart === "actual" && ms(d.from) <= nowMs0) continue;
    if (ctx.current.get(i.key)?.locked) continue;
    ctx.frozen.delete(i.key);
  }

  /* 1. keep what is frozen, and in a re-plan everything that is not broken */
  const broken = new Set(issues.map((i) => i.key));
  for (const a of ctx.current.values()) {
    const d = ctx.demand.get(a.key)!;
    const keep = ctx.frozen.has(a.key) || (input.mode === "replan" && !broken.has(a.key)) || !allowed.has(d.kind);
    if (keep) {
      ctx.placed.set(a.key, a);
      ctx.board.add(a, d, ctx.frozen.has(a.key));
    }
  }

  /* 2. place the rest, stands first: a gate depends on its stand */
  const order: ResourceKind[] = ["position", "gate", "belt", "counter"];
  const touched: string[] = [];
  for (const kind of order) {
    if (!allowed.has(kind)) continue;
    const todo = [...ctx.demand.values()].filter((d) => d.kind === kind && !ctx.placed.has(d.key) && !d.done);
    if (kind === "gate") for (const d of ctx.demand.values()) if (d.kind === "gate") d.positionCode = positionOf(ctx, d);
    const feasibleCount = (d: Demand) => candidates(ctx, d).filter((c) => !refusalsFor(ctx, d, c).length).length;
    const scored = todo.map((d) => ({ d, n: feasibleCount(d) }));
    scored.sort((a, b) => a.n - b.n || sizeIx(b.d.size) - sizeIx(a.d.size) || a.d.from.localeCompare(b.d.from));
    const stuck: Demand[] = [];
    for (const { d } of scored) {
      const best = bestFor(ctx, d);
      if (best) {
        place(ctx, d, best.codes, "engine");
        touched.push(d.key);
      } else stuck.push(d);
    }
    for (const d of stuck) {
      if (tryEject(ctx, d) || tryTow(ctx, d)) touched.push(d.key);
    }
    const scope = input.mode === "plan" ? [...ctx.placed.keys()].filter((k) => ctx.demand.get(k)?.kind === kind) : touched.filter((k) => ctx.demand.get(k)?.kind === kind);
    improve(ctx, scope);
    /* gates whose stand changed are re-derived */
    if (kind === "position") {
      for (const d of ctx.demand.values()) {
        if (d.kind !== "gate" || !ctx.placed.has(d.key) || d.done) continue;
        d.positionCode = positionOf(ctx, d);
        const a = ctx.placed.get(d.key)!;
        if (a.locked) continue;
        /* the stand moved: its gate follows, even inside the freeze */
        if (refusalsFor(ctx, d, a.segments[0].codes).some((r) => r.kind === "link")) {
          ctx.frozen.delete(d.key);
          unplace(ctx, d.key);
        }
      }
    }
  }

  /* 3. what moved, and what it costs people */
  const moves: Move[] = [];
  let lateGate = 0;
  const nowMs = input.now.getTime();
  for (const [k, a] of ctx.placed) {
    const cur = ctx.current.get(k);
    const d = ctx.demand.get(k)!;
    const to = a.segments.map((s) => s.codes.join("+"));
    const from = cur ? cur.segments.map((s) => s.codes.join("+")) : [];
    if (cur && from.join() === to.join()) continue;
    if (!cur && input.mode === "plan") continue;
    const issue = issues.find((i) => i.key === k);
    const follows =
      d.kind === "gate" && !issue && cur
        ? d.positionCode
          ? t("follows its stand, now {code}", { code: d.positionCode })
          : t("its flight has no stand; a bus gate until it has one")
        : undefined;
    let impact: string | undefined;
    if (d.kind === "gate" && cur && d.departureAt && ms(d.departureAt) - nowMs < 60 * MIN) {
      lateGate++;
      impact = t("gate change {n} min before departure", { n: Math.max(0, Math.round((ms(d.departureAt) - nowMs) / MIN)) });
    } else if (d.kind === "position") {
      const was = cur ? ctx.res.get(cur.segments[0].codes[0]) : undefined;
      const now = ctx.res.get(a.segments[0].codes[0]);
      if (was?.servedInPlace && !now?.servedInPlace && d.serviceType === "pax") impact = t("{n} people by bus instead of a bridge", { n: d.load });
    }
    moves.push({
      key: k,
      kind: d.kind,
      label: d.label,
      from,
      to,
      why: issue?.why ?? follows ?? (cur ? t("makes room for another flight") : t("had none")),
      at: d.from,
      impact,
      tow: a.tow,
    });
  }
  for (const k of ctx.current.keys()) {
    if (ctx.placed.has(k)) continue;
    const d = ctx.demand.get(k)!;
    const cur = ctx.current.get(k)!;
    moves.push({
      key: k,
      kind: d.kind,
      label: d.label,
      from: cur.segments.map((s) => s.codes.join("+")),
      to: [],
      why: issues.find((i) => i.key === k)?.why ?? t("could not be kept"),
      at: d.from,
    });
  }
  moves.sort((a, b) => a.at.localeCompare(b.at));

  const explain: Record<string, Explanation> = {};
  if (input.explain !== false) for (const [k, a] of ctx.placed) {
    const d = ctx.demand.get(k)!;
    if (d.done) continue;
    explain[k] = explainFor(ctx, d, a.segments.at(-1)!.codes, a.segments.length > 1 ? a.segments : undefined);
  }
  const unplaced = [...ctx.demand.values()]
    .filter((d) => !d.done && !ctx.placed.has(d.key))
    .map((d) => {
      const rs = candidates(ctx, d).flatMap((c) => refusalsFor(ctx, d, c));
      return {
        key: d.key,
        why: t("No {kind} for {what}: {why}", { kind: t(KIND_NOUN[d.kind]), what: d.label, why: refusalText(rs) }),
        refusals: rs,
      };
    });
  return {
    assignments: [...ctx.placed.values()].sort((a, b) => a.key.localeCompare(b.key)),
    explain,
    unplaced,
    issues,
    moves,
    score: scoreOf(ctx, moves.length, lateGate),
    before,
  };
}

function positionOf(ctx: Ctx, gate: Demand): string | undefined {
  const mid = gate.movementIds[0];
  for (const d of ctx.demand.values()) {
    if (d.kind !== "position" || !d.movementIds.includes(mid)) continue;
    const a = ctx.placed.get(d.key);
    return a?.segments.at(-1)?.codes[0];
  }
  return undefined;
}

/* ── automation ───────────────────────────────────────────────────────── */

export interface Decision {
  apply: boolean;
  why: string[];
}

/**
 * Whether a re-plan may be applied without a person. Every reason it may
 * not is listed — "it did not apply itself" is not an answer anybody can act on.
 */
export function decide(result: EngineResult, policies: Record<ResourceKind, Policy>): Decision {
  const why: string[] = [];
  if (!result.moves.length) return { apply: false, why: [t("Nothing to change.")] };
  const kinds = new Set(result.moves.map((m) => m.kind));
  for (const k of kinds) {
    const p = policies[k];
    if (p.automation !== "auto")
      why.push(p.automation === "off" ? t("{kind} are planned by hand.", { kind: t(KIND_LABEL[k]) }) : t("{kind} are set to advise only.", { kind: t(KIND_LABEL[k]) }));
    const n = result.moves.filter((m) => m.kind === k).length;
    if (n > p.maxAutoMoves) why.push(t("{n} changes to {kind}; at most {max} are made without a person.", { n, kind: t(KIND_LABEL[k]), max: p.maxAutoMoves }));
  }
  if (result.moves.some((m) => m.tow)) why.push(t("It needs a tow, and a tow is always a person's call."));
  if (result.score.lateGateChanges) why.push(t("It changes a gate after passengers were told."));
  if (result.moves.some((m) => m.impact && m.kind === "position")) why.push(t("It sends people to a bus instead of a bridge."));
  const b = result.before;
  if (b && (result.score.conflicts > b.conflicts || result.score.unplaced > b.unplaced)) why.push(t("It would leave the plan worse than it is."));
  return { apply: why.length === 0, why };
}

/* ── what if, and when ────────────────────────────────────────────────── */

export type WhatIf =
  | { kind: "close"; code: string; from: string; to: string; why?: string }
  | { kind: "delay"; movementId: string; minutes: number };

export function withChange(input: EngineInput, change: WhatIf): EngineInput {
  if (change.kind === "close") {
    return {
      ...input,
      mode: "replan",
      resources: input.resources.map((r) =>
        r.code === change.code
          ? { ...r, closed: [...(r.closed ?? []), { from: change.from, to: change.to, why: change.why ?? t("what-if closure") }] }
          : r,
      ),
    };
  }
  const shift = change.minutes * MIN;
  return {
    ...input,
    mode: "replan",
    demands: input.demands.map((d) => {
      if (!d.movementIds.includes(change.movementId)) return d;
      const first = d.movementIds[0] === change.movementId;
      /* an arrival late moves the whole stay's start; a departure late moves its end */
      if (d.kind === "position" && d.movementIds.length === 2)
        return first
          ? { ...d, from: iso(ms(d.from) + shift), to: iso(Math.max(ms(d.to), ms(d.from) + shift + 35 * MIN)) }
          : { ...d, to: iso(ms(d.to) + shift) };
      if (d.kind === "counter") return d;
      return { ...d, from: iso(ms(d.from) + shift), to: iso(ms(d.to) + shift), departureAt: d.departureAt ? iso(ms(d.departureAt) + shift) : undefined };
    }),
  };
}

export interface Window {
  from: string;
  to: string;
  moves: number;
  peopleBussed: number;
  unplaced: number;
  cost: number;
  text: string;
}

/**
 * The cheapest time to close a resource for maintenance: every start in
 * the span, re-planned around the closure, costed, best first.
 */
export function bestWindows(input: EngineInput, code: string, minutes: number, span: { from: string; to: string }, stepMin = 30): Window[] {
  const out: Window[] = [];
  const kind = input.resources.find((r) => r.code === code)?.kind ?? "position";
  const kinds: ResourceKind[] = kind === "position" ? ["position", "gate"] : [kind];
  /* only what the closure can move is re-planned — a whole day's desks and belts, forty-eight times over, was too slow */
  const keep = new Set(input.demands.filter((d) => kinds.includes(d.kind)).map((d) => d.key));
  input = {
    ...input,
    explain: false,
    kinds,
    demands: input.demands.filter((d) => keep.has(d.key)),
    current: input.current.filter((a) => keep.has(a.key)),
  };
  /* each window is costed against ONE plan, so a trial re-places only what its closure breaks */
  const planned = solve({ ...input, mode: "replan" });
  input = { ...input, current: planned.assignments };
  const base = planned.score;
  for (let s = ms(span.from); s + minutes * MIN <= ms(span.to); s += stepMin * MIN) {
    const w = { kind: "close" as const, code, from: iso(s), to: iso(s + minutes * MIN), why: t("maintenance") };
    const r = solve(withChange(input, w));
    const extraBus = Math.max(0, r.score.peopleBussed - base.peopleBussed);
    const lost = Math.max(0, r.score.unplaced - base.unplaced);
    const cost = lost * 1000 + r.moves.length * 10 + extraBus * 0.2 + Math.max(0, r.score.expectedConflicts - base.expectedConflicts) * 50 + r.score.lateGateChanges * 40;
    out.push({
      from: w.from,
      to: w.to,
      moves: r.moves.length,
      peopleBussed: extraBus,
      unplaced: lost,
      cost: Math.round(cost),
      text: lost
        ? tn(lost, "{n} flight left without a place", "{n} flights left without a place")
        : r.moves.length
          ? t("{moves} · {bus}", {
              moves: tn(r.moves.length, "{n} change", "{n} changes"),
              bus: extraBus ? t("{n} more people bussed", { n: extraBus }) : t("nobody bussed"),
            })
          : t("nothing moves"),
    });
  }
  return out.sort((a, b) => a.cost - b.cost || a.from.localeCompare(b.from));
}

/* ── the learning ─────────────────────────────────────────────────────── */

/** A person placed this demand here over the engine's choice: remember it for the operator. */
export function learnFrom(learned: readonly Learned[], d: Demand, code: string): Learned[] {
  const i = learned.findIndex((l) => l.kind === d.kind && l.operator === d.operator && l.code === code);
  if (i < 0) return [...learned, { kind: d.kind, operator: d.operator, code, n: 1 }];
  return learned.map((l, j) => (j === i ? { ...l, n: l.n + 1 } : l));
}

/** Checks a person's own placement, with every reason it would be wrong. */
export function checkPlacement(input: EngineInput, key: string, codes: string[]): Refusal[] {
  const ctx = makeCtx({ ...input, mode: "replan" });
  for (const a of ctx.current.values()) {
    if (a.key === key) continue;
    ctx.placed.set(a.key, a);
    ctx.board.add(a, ctx.demand.get(a.key)!, false);
  }
  const d = ctx.demand.get(key);
  if (!d) return [{ code: codes[0] ?? "", kind: "none", why: t("That flight is not on today's plan.") }];
  if (d.kind === "gate") d.positionCode = positionOf(ctx, d);
  return refusalsFor(ctx, d, codes);
}

/** The plan after a re-plan, with what moved marked by who moved it. */
export function applyMoves(result: EngineResult, source: Assignment["source"]): Assignment[] {
  const moved = new Set(result.moves.map((m) => m.key));
  return result.assignments.map((a) => (moved.has(a.key) ? { ...a, source } : a));
}

/**
 * The plan as it stands, judged: what is wrong with it, its score, and why
 * each placement is where it is.
 */
export function assess(input: EngineInput): { issues: Issue[]; score: Score; explain: Record<string, Explanation> } {
  const ctx = makeCtx({ ...input, mode: "plan" });
  for (const a of ctx.current.values()) {
    ctx.placed.set(a.key, a);
    ctx.board.add(a, ctx.demand.get(a.key)!, ctx.frozen.has(a.key));
  }
  const issues = issuesOf(ctx);
  const explain: Record<string, Explanation> = {};
  if (input.explain !== false)
    for (const [k, a] of ctx.placed) {
      const d = ctx.demand.get(k)!;
      if (d.done) continue;
      explain[k] = explainFor(ctx, d, a.segments.at(-1)!.codes, a.segments.length > 1 ? a.segments : undefined);
    }
  return { issues, score: scoreOf(ctx, 0, 0), explain };
}

/* ── what the platform returns ────────────────────────────────────────── */

export interface Proposal {
  id: string;
  createdAt: string;
  trigger: string;
  moves: Move[];
  before: Score;
  after: Score;
  decision: Decision;
  status: "pending" | "applied" | "rejected" | "auto_applied" | "expired";
  assignments: Assignment[];
  decidedBy?: string;
  decidedAt?: string;
  note?: string;
}

export interface LogEntry {
  at: string;
  kind: ResourceKind;
  label: string;
  from: string[];
  to: string[];
  source: "engine" | "person" | "auto";
  by?: string;
  why: string;
}

export interface Trust {
  proposed: number;
  applied: number;
  rejected: number;
  auto: number;
}

export interface AllocDay {
  now: string;
  horizon: { from: string; to: string };
  resources: Resource[];
  demands: Demand[];
  plan: Assignment[];
  explain: Record<string, Explanation>;
  issues: Issue[];
  score: Score;
  proposal: Proposal | null;
  policies: Policy[];
  rules: Rule[];
  learned: Learned[];
  log: LogEntry[];
  trust: Trust;
  sample: boolean;
}

/**
 * Whether the record of decisions supports letting a kind apply itself: at
 * least ten proposals decided, and nine in ten accepted as proposed.
 */
export function trustAdvice(trust: Trust): string | null {
  const decided = trust.applied + trust.rejected;
  if (decided < 10) return null;
  const rate = trust.applied / decided;
  return rate >= 0.9
    ? t("Controllers accepted {pct}% of the last {n} proposals as they were — these could be applied automatically.", {
        pct: Math.round(rate * 100),
        n: decided,
      })
    : null;
}
